import type { DataCollection, SpanJSON, StreamedSpanJSON } from '@sentry/core'
import type { Breadcrumb, ErrorEvent, ReplayFrameEvent } from '@sentry/nextjs'

/** Shared, runtime-agnostic Sentry privacy policy for every CargoKing runtime. */

export const REDACTED = '[Filtered]'

const SENSITIVE_KEY_PARTS = new Set([
  'address',
  'auth',
  'authorization',
  'body',
  'canary',
  'company',
  'cookie',
  'csrf',
  'customer',
  'email',
  'form',
  'fragment',
  'header',
  'headers',
  'id',
  'identity',
  'jwt',
  'name',
  'password',
  'phone',
  'query',
  'request',
  'response',
  'secret',
  'session',
  'token',
  'user',
  'username',
  'values',
  'vat',
  'xsrf',
])

const URL_VALUE_PATTERN = /^(?:[a-z][a-z\d+.-]*:\/\/|\/|\.\/|\.\.\/)/i
const CUSTOMER_SETUP_TOKEN_PATH_PATTERN =
  /(\/setup-forms\/customers\/tokens\/)[A-Za-z0-9]{64}(?=\/|$)/g
const SENSITIVE_VALUE_PATTERN =
  /(?:\bBearer\s+\S+|\beyJ[A-Za-z\d_-]*\.[A-Za-z\d_-]+\.[A-Za-z\d_-]+\b|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|\b(?:\+?\d[\d ()-]{7,}\d)\b|\b(?:GB)?\d{9,12}\b|canary|(?:password|secret|token|jwt|cookie|authorization)\s*[:=]\s*\S+)/i

const SAFE_STRING_KEYS = new Set([
  'addr_mode',
  'architecture',
  'browser',
  'category',
  'debug_id',
  'device',
  'end_reason',
  'environment',
  'filename',
  'function',
  'instruction_addr',
  'level',
  'logger',
  'mechanism',
  'method',
  'module',
  'op',
  'origin',
  'os',
  'package',
  'platform',
  'reason',
  'release',
  'runtime',
  'span_id',
  'status',
  'status_code',
  'trace_id',
  'type',
  'version',
])

const SAFE_SCALAR_KEYS = new Set([
  'block_all_media',
  'colno',
  'crashed',
  'current',
  'duration',
  'end_timestamp',
  'error_sample_rate',
  'exclusive_time',
  'handled',
  'in_app',
  'is_segment',
  'lineno',
  'main',
  'mask_all_inputs',
  'mask_all_text',
  'network_capture_bodies',
  'network_detail_has_urls',
  'network_request_has_headers',
  'network_response_has_headers',
  'sample_rate',
  'sampled',
  'session_sample_rate',
  'should_record_canvas',
  'start_timestamp',
  'status_code',
  'synthetic',
  'timestamp',
  'use_compression',
  'use_compression_option',
])

const SAFE_URL_KEYS = new Set([
  'abs_path',
  'code_file',
  'debug_file',
  'filename',
  'from',
  'href',
  'to',
  'uri',
  'url',
  'url_full',
  'url_path',
])
const NEXT_DIGEST_PATTERN = /^\d{1,10}(?:@E\d{1,6})?$/

const SAFE_HTTP_METHODS = new Set(['DELETE', 'GET', 'HEAD', 'OPTIONS', 'PATCH', 'POST', 'PUT'])

/**
 * Streamed span attributes that replaced top-level v10 span and transaction fields, mapped to the
 * key whose sanitization rules those fields followed before.
 */
const SENTRY_SPAN_ATTRIBUTE_KEYS = new Map([
  ['sentry.environment', 'environment'],
  ['sentry.op', 'op'],
  ['sentry.origin', 'origin'],
  ['sentry.release', 'release'],
  ['sentry.sdk.name', 'package'],
  ['sentry.sdk.version', 'version'],
  ['sentry.segment.id', 'span_id'],
  ['sentry.segment.name', 'url'],
  ['sentry.segment.name.source', 'type'],
])

const V10_DENIED_KEYS = ['forwarded', '-ip', 'remote-', 'via', '-user']

/**
 * Sentry v11 collects user info, cookies, headers, bodies, and query data by default. This is the
 * migration guide's explicit v10 default baseline; `beforeSend*` scrubbing still applies on top.
 */
export const dataCollection: DataCollection = {
  cookies: false,
  databaseQueryData: false,
  genAI: { inputs: false, outputs: false },
  graphQL: { document: false, variables: false },
  httpBodies: [],
  httpHeaders: {
    request: { deny: V10_DENIED_KEYS },
    response: { deny: V10_DENIED_KEYS },
  },
  queues: false,
  urlQueryParams: { deny: V10_DENIED_KEYS },
  userInfo: false,
}

/** Remove query strings and fragments without attempting to normalize the URL. */
export const stripUrlSensitiveParts = (url: string): string => {
  const queryIndex = url.indexOf('?')
  const fragmentIndex = url.indexOf('#')
  const cutAt = [queryIndex, fragmentIndex].filter(index => index >= 0)

  return cutAt.length === 0 ? url : url.slice(0, Math.min(...cutAt))
}

const sanitizeUrl = (url: string): string =>
  stripUrlSensitiveParts(url).replace(CUSTOMER_SETUP_TOKEN_PATH_PATTERN, `$1${REDACTED}`)

const isRecord = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false

  try {
    const prototype: unknown = Object.getPrototypeOf(value)
    return prototype === Object.prototype || prototype === null
  } catch {
    return false
  }
}

type ToJSONResult =
  | { status: 'absent' }
  | { status: 'failed' }
  | { status: 'success'; value: unknown }

const callToJSON = (value: object): ToJSONResult => {
  try {
    const toJSON: unknown = Reflect.get(value, 'toJSON')
    if (typeof toJSON !== 'function') return { status: 'absent' }

    return { status: 'success', value: Reflect.apply(toJSON, value, []) }
  } catch {
    return { status: 'failed' }
  }
}

const normalizeKey = (key: string): string =>
  key
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z\d]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()

const isSensitiveKey = (key: string): boolean => {
  const normalizedKey = normalizeKey(key)
  if (SAFE_STRING_KEYS.has(normalizedKey) || SAFE_SCALAR_KEYS.has(normalizedKey)) return false

  return normalizedKey.split('_').some(part => SENSITIVE_KEY_PARTS.has(part))
}

const sanitizeString = (value: string, key: string): string => {
  const normalizedKey = normalizeKey(key)

  if (SAFE_URL_KEYS.has(normalizedKey) || URL_VALUE_PATTERN.test(value)) {
    const sanitizedUrl = sanitizeUrl(value)
    return SENSITIVE_VALUE_PATTERN.test(sanitizedUrl) ? REDACTED : sanitizedUrl
  }

  if (SENSITIVE_VALUE_PATTERN.test(value)) return REDACTED

  if (normalizedKey === 'method') {
    const method = value.toUpperCase()
    return SAFE_HTTP_METHODS.has(method) ? method : REDACTED
  }

  return SAFE_STRING_KEYS.has(normalizedKey) ? value : REDACTED
}

/**
 * Recursively sanitize SDK metadata. Only explicitly allowlisted SDK metadata scalars survive;
 * business identifiers and unknown scalar values are removed or redacted regardless of their type.
 */
const sanitizeNestedValueInternal = (
  value: unknown,
  key: string,
  ancestors: WeakSet<object>,
): unknown => {
  const normalizedKey = normalizeKey(key)

  if (isSensitiveKey(key)) return REDACTED

  if (typeof value === 'string') return sanitizeString(value, key)
  if (typeof value === 'number' || typeof value === 'boolean') {
    return SAFE_SCALAR_KEYS.has(normalizedKey) ? value : undefined
  }
  if (value === null) return undefined
  if (typeof value !== 'object') return undefined
  if (ancestors.has(value)) return undefined

  ancestors.add(value)

  try {
    const toJSONResult = callToJSON(value)
    if (toJSONResult.status === 'failed') return undefined
    if (toJSONResult.status === 'success') {
      return sanitizeNestedValueInternal(toJSONResult.value, key, ancestors)
    }

    if (Array.isArray(value)) {
      return value
        .map(item => sanitizeNestedValueInternal(item, key, ancestors))
        .filter(item => item !== undefined)
    }

    if (isRecord(value)) {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([nestedKey]) => !SENSITIVE_VALUE_PATTERN.test(nestedKey))
          .map(([nestedKey, nestedValue]) => [
            nestedKey,
            sanitizeNestedValueInternal(nestedValue, nestedKey, ancestors),
          ]),
      )
    }
  } catch {
    return undefined
  } finally {
    ancestors.delete(value)
  }

  return undefined
}

export const sanitizeNestedValue = (value: unknown, key = ''): unknown =>
  sanitizeNestedValueInternal(value, key, new WeakSet())

/**
 * Sanitize an object slot of an SDK payload. A result that is not a plain object (a redacted
 * string, an array, or custom toJSON output) is dropped instead of taking the object's place.
 *
 * The result keeps a subset of the original keys with scrubbed values, so it no longer matches the
 * slot's precise SDK type. That is safe only for slots the SDK JSON-serializes after the hook
 * returns and that no code reads as typed values; the SAFETY casts below rely on this.
 */
const sanitizeRecord = <T extends object>(value: T): Record<string, unknown> | undefined => {
  const sanitized = sanitizeNestedValue(value)
  return isRecord(sanitized) ? sanitized : undefined
}

/** Drop console breadcrumbs and remove all free-text from the remaining breadcrumb metadata. */
export const sanitizeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
  if (breadcrumb.category?.toLowerCase() === 'console') return null

  return {
    category: breadcrumb.category
      ? sanitizeString(breadcrumb.category, 'category')
      : breadcrumb.category,
    data: breadcrumb.data ? sanitizeRecord(breadcrumb.data) : breadcrumb.data,
    level: breadcrumb.level,
    message: undefined,
    timestamp: breadcrumb.timestamp,
    type: breadcrumb.type ? sanitizeString(breadcrumb.type, 'type') : breadcrumb.type,
  }
}

const sanitizeRequest = (event: ErrorEvent): void => {
  if (!event.request) return

  event.request = {
    method: event.request.method
      ? sanitizeString(event.request.method, 'method')
      : event.request.method,
    url: event.request.url ? sanitizeString(event.request.url, 'url') : event.request.url,
  }
}

const sanitizeEventBreadcrumbs = (event: ErrorEvent): void => {
  if (!event.breadcrumbs) return

  event.breadcrumbs = event.breadcrumbs
    .map(sanitizeBreadcrumb)
    .filter((breadcrumb): breadcrumb is Breadcrumb => breadcrumb !== null)
}

const sanitizeFrames = (value: unknown): unknown => {
  if (!isRecord(value)) return sanitizeNestedValue(value)

  const sanitized = sanitizeRecord(value)
  if (!sanitized) return undefined
  delete sanitized.vars
  delete sanitized.pre_context
  delete sanitized.context_line
  delete sanitized.post_context

  if (typeof value.filename === 'string') {
    sanitized.filename = sanitizeString(value.filename, 'url')
  }
  if (typeof value.abs_path === 'string') {
    sanitized.abs_path = sanitizeString(value.abs_path, 'url')
  }

  return sanitized
}

const sanitizeStacktrace = <T extends { frames?: unknown[]; frames_omitted?: [number, number] }>(
  stacktrace: T,
): T =>
  // SAFETY: serialized-only SDK slot (see sanitizeRecord); frames are never read as typed values.
  ({
    frames: stacktrace.frames?.map(sanitizeFrames),
    frames_omitted: stacktrace.frames_omitted,
  }) as T

const sanitizeThreads = (event: ErrorEvent): void => {
  if (!event.threads) return

  event.threads = {
    values: event.threads.values.map(thread => ({
      crashed: thread.crashed,
      current: thread.current,
      main: thread.main,
      stacktrace: thread.stacktrace ? sanitizeStacktrace(thread.stacktrace) : undefined,
    })),
  }
}

const sanitizeExceptions = (event: ErrorEvent): void => {
  event.message = undefined
  event.logentry = undefined

  for (const exception of event.exception?.values ?? []) {
    exception.module = exception.module
      ? sanitizeString(exception.module, 'module')
      : exception.module
    exception.thread_id = undefined
    exception.type = exception.type ? sanitizeString(exception.type, 'type') : exception.type
    exception.value = exception.value ? REDACTED : exception.value
    if (exception.mechanism) {
      // SAFETY: serialized-only SDK slot (see sanitizeRecord); `type` is an allowlisted string key.
      exception.mechanism = sanitizeNestedValue(exception.mechanism) as typeof exception.mechanism
    }
    if (exception.stacktrace?.frames) {
      exception.stacktrace = sanitizeStacktrace(exception.stacktrace)
    }
  }
}

const sanitizeEventMetadata = (event: ErrorEvent): void => {
  event.user = undefined
  // SAFETY: serialized-only SDK slot (see sanitizeRecord).
  event.contexts = event.contexts
    ? (sanitizeRecord(event.contexts) as typeof event.contexts)
    : event.contexts
  // Custom extras and fingerprints have no approved correlation fields and are entirely untrusted.
  event.extra = undefined
  event.fingerprint = undefined
  const digest = event.tags?.digest
  event.tags =
    typeof digest === 'string' && NEXT_DIGEST_PATTERN.test(digest) ? { digest } : undefined

  sanitizeRequest(event)
  sanitizeEventBreadcrumbs(event)
}

export const sanitizeErrorEvent = (event: ErrorEvent): ErrorEvent => {
  sanitizeEventMetadata(event)
  sanitizeExceptions(event)
  sanitizeThreads(event)

  if (event.transaction) event.transaction = sanitizeString(event.transaction, 'url')
  if (event.spans) event.spans = event.spans.map(sanitizeStaticSpan)

  event.environment = event.environment
    ? sanitizeString(event.environment, 'environment')
    : event.environment
  event.logger = event.logger ? sanitizeString(event.logger, 'logger') : event.logger
  event.platform = event.platform ? sanitizeString(event.platform, 'platform') : event.platform
  event.release = event.release ? sanitizeString(event.release, 'release') : event.release

  // These top-level extension points can contain arbitrary names, paths, or values. Stack/debug
  // locations remain available through the explicitly sanitized exception and thread frames.
  event.debug_meta = undefined
  event.dist = undefined
  event.measurements = undefined
  event.modules = undefined
  event.sdkProcessingMetadata = undefined
  event.server_name = undefined
  return event
}

type SpanLink = { attributes?: object; sampled?: boolean; span_id: string; trace_id: string }

const sanitizeLink = <T extends SpanLink>(link: T): T =>
  // SAFETY: serialized-only SDK slot (see sanitizeRecord); T only varies the attributes type.
  ({
    attributes: link.attributes ? sanitizeRecord(link.attributes) : undefined,
    sampled: link.sampled,
    span_id: link.span_id,
    trace_id: link.trace_id,
  }) as T

const sanitizeSpanDescription = (description: string): string =>
  URL_VALUE_PATTERN.test(description) ? sanitizeString(description, 'url') : REDACTED

/** Sanitize transaction-format spans, which v11 only attaches to error events. */
const sanitizeStaticSpan = (span: SpanJSON): SpanJSON => ({
  // SAFETY: serialized-only SDK slot (see sanitizeRecord).
  data: (sanitizeRecord(span.data) ?? {}) as SpanJSON['data'],
  description: span.description ? sanitizeSpanDescription(span.description) : span.description,
  exclusive_time: span.exclusive_time,
  is_segment: span.is_segment,
  links: span.links?.map(sanitizeLink),
  op: span.op ? sanitizeString(span.op, 'op') : span.op,
  // Keep the typed origin only when scrubbing leaves it unchanged; otherwise drop it.
  origin:
    span.origin && sanitizeString(span.origin, 'origin') === span.origin ? span.origin : undefined,
  parent_span_id: span.parent_span_id,
  profile_id: span.profile_id,
  segment_id: span.segment_id,
  span_id: span.span_id,
  start_timestamp: span.start_timestamp,
  status: span.status ? sanitizeString(span.status, 'status') : span.status,
  timestamp: span.timestamp,
  trace_id: span.trace_id,
})

const sanitizeSpanAttributes = (
  attributes: StreamedSpanJSON['attributes'],
): StreamedSpanJSON['attributes'] => {
  const sanitized = sanitizeRecord(attributes) ?? {}

  for (const [key, fieldKey] of SENTRY_SPAN_ATTRIBUTE_KEYS) {
    if (key in attributes) sanitized[key] = sanitizeNestedValue(attributes[key], fieldKey)
  }

  return sanitized
}

/**
 * `beforeSendSpan` for v11 streamed spans, which replace transaction events. Every span, including
 * the segment span that used to be the transaction, is scrubbed here as it is sent: user, request,
 * query, and context attributes are redacted, and the segment name keeps the transaction name rules.
 */
export const sanitizeSpan = (span: StreamedSpanJSON): StreamedSpanJSON => ({
  attributes: sanitizeSpanAttributes(span.attributes),
  end_timestamp: span.end_timestamp,
  is_segment: span.is_segment,
  links: span.links?.map(sanitizeLink),
  name: span.is_segment ? sanitizeString(span.name, 'url') : sanitizeSpanDescription(span.name),
  parent_span_id: span.parent_span_id,
  span_id: span.span_id,
  start_timestamp: span.start_timestamp,
  status: span.status,
  trace_id: span.trace_id,
})

/**
 * SDK-supported Replay recording callback. Unknown/custom frames and console frames are dropped;
 * navigation and resource URLs are scrubbed, while arbitrary breadcrumb/network metadata is redacted.
 */
export const sanitizeReplayRecordingEvent = (event: ReplayFrameEvent): null | ReplayFrameEvent => {
  if (event.data.tag === 'options') {
    const payload = event.data.payload
    return {
      data: {
        payload: {
          blockAllMedia: payload.blockAllMedia,
          errorSampleRate: payload.errorSampleRate,
          maskAllInputs: payload.maskAllInputs,
          maskAllText: payload.maskAllText,
          networkCaptureBodies: payload.networkCaptureBodies,
          networkDetailHasUrls: payload.networkDetailHasUrls,
          networkRequestHasHeaders: payload.networkRequestHasHeaders,
          networkResponseHasHeaders: payload.networkResponseHasHeaders,
          sessionSampleRate: payload.sessionSampleRate,
          shouldRecordCanvas: payload.shouldRecordCanvas,
          useCompression: payload.useCompression,
          useCompressionOption: payload.useCompressionOption,
        },
        tag: 'options',
      },
      timestamp: event.timestamp,
      type: event.type,
    }
  }
  if (event.data.tag !== 'breadcrumb' && event.data.tag !== 'performanceSpan') return null

  const payload = event.data.payload

  if ('category' in payload) {
    if (payload.category.toLowerCase() === 'console') return null

    return {
      data: {
        payload: {
          category: sanitizeString(payload.category, 'category'),
          // SAFETY: serialized-only SDK slot (see sanitizeRecord).
          data: payload.data ? (sanitizeRecord(payload.data) as typeof payload.data) : payload.data,
          message: undefined,
          timestamp: payload.timestamp,
          type: sanitizeString(payload.type, 'type'),
        },
        tag: 'breadcrumb',
      },
      timestamp: event.timestamp,
      type: event.type,
    }
  }

  const description = URL_VALUE_PATTERN.test(payload.description)
    ? sanitizeString(payload.description, 'url')
    : REDACTED
  // SAFETY: serialized-only SDK slot (see sanitizeRecord).
  const data =
    payload.op === 'resource.fetch' || payload.op === 'resource.xhr'
      ? undefined
      : payload.data
        ? (sanitizeRecord(payload.data) as typeof payload.data)
        : payload.data

  return {
    data: {
      payload: {
        data,
        description,
        endTimestamp: payload.endTimestamp,
        op: sanitizeString(payload.op, 'op'),
        startTimestamp: payload.startTimestamp,
      },
      tag: 'performanceSpan',
    },
    timestamp: event.timestamp,
    type: event.type,
  }
}

export const getTracesSampleRate = (environment?: string): number =>
  environment === 'development' ? 1 : 0.1

export const assertSentryRuntimeConfig = ({
  dsn,
  enabled,
}: {
  dsn?: string
  enabled: boolean
}): void => {
  if (!enabled) return

  if (!dsn) {
    throw new Error(
      'Sentry telemetry is enabled but no DSN was provided. Set the DSN or disable telemetry.',
    )
  }
}

export const isTelemetryEnabled = (flag?: string): boolean => flag === 'true'

/** Immutable release identity shared by runtime initialization and source-map upload. */
export const getSentryRelease = ({
  app,
  sha,
  version,
}: {
  app: string
  sha?: string
  version: string
}): string => (sha ? `${app}@${version}+${sha}` : `${app}@${version}`)
